import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Boxes } from "lucide-react";
import { useBomForm } from "./useBomForm";
import { BomIdentityFields } from "./form/BomIdentityFields";
import { BomImageField } from "./form/BomImageField";
import { BomStockFields } from "./form/BomStockFields";
import { BomSourcingFields } from "./form/BomSourcingFields";
import { BomLineField } from "./form/BomLineField";
import { BomComponentsEditor } from "./form/BomComponentsEditor";
import { BomStagesEditor } from "./form/BomStagesEditor";

function saveLabel(saving, isEdit) {
  if (saving) return "Saving…";
  return isEdit ? "Save Changes" : "Create Item";
}

export default function BomFormModal({ open, item, template = null, items, suppliers = [], machines = [], onOpenChange, onSubmit }) {
  const f = useBomForm({ open, item, template, items, suppliers, onSubmit, onOpenChange });
  const isMfg = f.form.item_type === "manufactured" || f.form.item_type === "mfg_spare";
  let title = "New BOM Item";
  if (item) title = `Edit ${item.sku}`;
  else if (template) title = `Duplicate of ${template.sku}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[80vh] flex flex-col overflow-hidden" data-testid="bom-form-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Boxes className="h-4 w-4 text-blue-600" />
            {title}
          </DialogTitle>
          {template && <p className="text-xs text-slate-500" data-testid="bom-duplicate-hint">Prefilled from {template.name} — change the name/SKU, then create. Stock starts at 0.</p>}
        </DialogHeader>
        <form onSubmit={f.submit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-2" data-testid="bom-form-body">
          <BomIdentityFields form={f.form} set={f.set} />
          <BomImageField imageUrl={f.form.image_url} onChange={(v) => f.set("image_url", v)} onFile={f.handleFile} />
          <BomStockFields form={f.form} set={f.set} isEdit={!!item} supplierOptions={f.supplierOptions} />
          {!isMfg && <BomSourcingFields form={f.form} set={f.set} />}
          {isMfg && <BomLineField value={f.form.line_id} onChange={(v) => f.set("line_id", v)} machines={machines} />}
          {isMfg && (
            <BomComponentsEditor components={f.form.components} options={f.candidateOptions}
              onAdd={() => f.addRow("components", { item_id: "", quantity_per_unit: 1 })}
              onChange={(i, k, v) => f.updateRow("components", i, k, v)} onRemove={(i) => f.removeRow("components", i)}
              onMove={(from, to) => f.moveRow("components", from, to)} />
          )}
          {isMfg && (
            <BomStagesEditor stages={f.form.stages}
              onAdd={() => f.addRow("stages", { name: "", type: "in_house" })}
              onChange={(i, k, v) => f.updateRow("stages", i, k, v)} onRemove={(i) => f.removeRow("stages", i)}
              onMove={(from, to) => f.moveRow("stages", from, to)} />
          )}
          </div>
          <DialogFooter className="shrink-0 pt-4 mt-3 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={f.saving} className="bg-blue-600 hover:bg-blue-700" data-testid="bom-form-submit">
              {saveLabel(f.saving, !!item)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
